import React, { useState } from 'react';
import { parseFraction, formatFraction } from '../utils/fractions';

/**
 * A controlled text input that accepts glass fraction measurements.
 * Displays as formatted fractions (e.g. "46 13/16\"") when not focused.
 * While focused, shows the raw string the user is typing.
 * On blur, parses to decimal and calls onChange(decimal | null).
 * Shows a red border on unparseable input.
 *
 * Props:
 *   value      — decimal number (or null / '')
 *   onChange   — called with decimal number, or null if cleared
 *   placeholder, style, className
 */
export default function FractionInput({ value, onChange, placeholder, style, className }) {
  const [editing, setEditing] = useState(false);
  const [rawText, setRawText] = useState('');
  const [invalid, setInvalid] = useState(false);

  const displayValue = (value != null && value !== '' && !isNaN(parseFloat(value)))
    ? formatFraction(parseFloat(value))
    : '';

  const handleFocus = () => {
    setEditing(true);
    setRawText(displayValue);
    setInvalid(false);
  };

  const handleChange = (e) => {
    setRawText(e.target.value);
    setInvalid(false);
  };

  const handleBlur = () => {
    setEditing(false);
    const trimmed = (rawText || '').trim();
    if (!trimmed) {
      setInvalid(false);
      onChange(null);
      return;
    }
    const parsed = parseFraction(trimmed);
    if (parsed == null || isNaN(parsed)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onChange(parsed);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
  };

  return (
    <input
      type="text"
      value={editing ? rawText : displayValue}
      placeholder={placeholder || '0"'}
      className={className}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{
        ...style,
        ...(invalid ? { borderColor: '#ef4444', outline: '1px solid #ef4444' } : {}),
      }}
    />
  );
}
